import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { FinanceAPI } from "@/lib/finance-api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, IndianRupee, PieChart as PieIcon, Wallet, Plus, Loader2 } from "lucide-react";
import ExpensesDialog from "@/components/finance/ExpensesDialog";

const PALETTE = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#84cc16", "#f97316",
];

const RANGES = [
  { v: "today", label: "Today" },
  { v: "yesterday", label: "Yesterday" },
  { v: "7d", label: "Last 7 days" },
  { v: "15d", label: "Last 15 days" },
  { v: "30d", label: "Last 30 days" },
  { v: "90d", label: "Last 90 days" },
  { v: "ytd", label: "Year to date" },
];

export default function FinanceDashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState("30d");
  const [hover, setHover] = useState(null);
  const [expensesOpen, setExpensesOpen] = useState(false);

  const summaryQ = useQuery({
    queryKey: ["fin-summary", range],
    queryFn: () => FinanceAPI.summary({ range }),
  });

  const data = useMemo(() => {
    const sum = summaryQ.data || { total: { revenue: 0, expense: 0, profit: 0 }, by_campus: [], by_category: [] };
    const list = (sum.by_campus || []).map((c, i) => ({ ...c, color: PALETTE[i % PALETTE.length] }));
    return { list, total: sum.total, byCategory: sum.by_category || [] };
  }, [summaryQ.data]);

  const totalRevenue = data.total.revenue || 1;
  let cumulative = 0;
  const slices = data.list.map((c) => {
    const startAngle = (cumulative / totalRevenue) * 360;
    cumulative += c.revenue;
    const endAngle = (cumulative / totalRevenue) * 360;
    return { ...c, startAngle, endAngle, pct: (c.revenue / totalRevenue) * 100 };
  });

  return (
    <AppShell>
      <div className="p-6 max-w-7xl mx-auto" data-testid="finance-360-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold tracking-tight text-3xl">Finance 360°</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Live Revenue · Expense · Profit/Loss across {data.list.length} campuses from real fee invoices & expenses.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-[170px]" data-testid="fin-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setExpensesOpen(true)} data-testid="fin-expenses-btn">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Expenses
            </Button>
          </div>
        </div>

        {summaryQ.isLoading ? (
          <div className="p-16 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading finance data…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <Kpi color="emerald" icon={<TrendingUp className="h-5 w-5" />} label="Revenue" value={data.total.revenue} testId="kpi-revenue" />
              <Kpi color="rose" icon={<TrendingDown className="h-5 w-5" />} label="Expense" value={data.total.expense} testId="kpi-expense" />
              <Kpi color={data.total.profit >= 0 ? "sky" : "rose"} icon={<Wallet className="h-5 w-5" />} label={data.total.profit >= 0 ? "Profit" : "Loss"} value={Math.abs(data.total.profit)} testId="kpi-profit" />
            </div>

            <div className="bg-card border border-border rounded-2xl shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <PieIcon className="h-4 w-4 text-primary" />
                <h2 className="font-semibold text-lg">Revenue split by campus</h2>
              </div>
              {data.list.length === 0 ? (
                <div className="text-sm text-muted-foreground py-12 text-center">
                  No revenue recorded in this range. Try a longer window or seed demo data.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-8 items-center">
                  <div className="relative">
                    <svg viewBox="0 0 200 200" className="w-full max-w-[360px] mx-auto">
                      {slices.map((s) => {
                        const isHover = hover === s.id;
                        const r = isHover ? 92 : 86;
                        return (
                          <path
                            key={s.id}
                            d={arcPath(100, 100, r, s.startAngle, s.endAngle)}
                            fill={s.color}
                            stroke="#fff"
                            strokeWidth="2"
                            className="cursor-pointer transition-all duration-300"
                            style={{ filter: isHover ? "drop-shadow(0 4px 12px rgba(0,0,0,0.25))" : "none" }}
                            onMouseEnter={() => setHover(s.id)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => navigate(`/campus-360?id=${s.id}`)}
                            data-testid={`pie-slice-${s.id}`}
                          />
                        );
                      })}
                      <circle cx="100" cy="100" r="48" fill="#fff" />
                      <text x="100" y="92" textAnchor="middle" fontSize="9" fill="#64748b">TOTAL REVENUE</text>
                      <text x="100" y="108" textAnchor="middle" fontSize="13" fontWeight="700" fill="#0f172a">₹{compactINR(data.total.revenue)}</text>
                    </svg>
                  </div>
                  <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                    {slices.map((s) => (
                      <button
                        key={s.id}
                        onMouseEnter={() => setHover(s.id)}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => navigate(`/campus-360?id=${s.id}`)}
                        className={`w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg transition border ${hover === s.id ? "border-primary/40 bg-primary/5 scale-[1.02]" : "border-transparent hover:bg-muted/40"}`}
                        data-testid={`pie-legend-${s.id}`}
                      >
                        <div className="h-4 w-4 rounded shrink-0" style={{ backgroundColor: s.color }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{s.name}</div>
                          <div className="text-[11px] text-muted-foreground">{s.pct.toFixed(1)}% of revenue · drill down →</div>
                        </div>
                        <div className="text-sm font-semibold tabular-nums">₹{compactINR(s.revenue)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <div className="lg:col-span-2 bg-card border border-border rounded-2xl shadow-sm p-6">
                <h2 className="font-semibold text-lg mb-3">Campus-wise profit & loss</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                        <th className="py-2">Campus</th>
                        <th className="py-2 text-right">Revenue</th>
                        <th className="py-2 text-right">Expense</th>
                        <th className="py-2 text-right">P / L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slices.map((s) => (
                        <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/campus-360?id=${s.id}`)} data-testid={`fin-row-${s.id}`}>
                          <td className="py-2.5 flex items-center gap-2"><div className="h-3 w-3 rounded" style={{ backgroundColor: s.color }} />{s.name}</td>
                          <td className="py-2.5 text-right tabular-nums">₹{(s.revenue || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                          <td className="py-2.5 text-right tabular-nums text-rose-600">₹{(s.expense || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                          <td className={`py-2.5 text-right tabular-nums font-semibold ${s.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                            {s.profit >= 0 ? "+" : "-"}₹{Math.abs(s.profit || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-card border border-border rounded-2xl shadow-sm p-6">
                <h2 className="font-semibold text-lg mb-3">Expense breakdown</h2>
                {data.byCategory.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-8 text-center">No expenses logged in this range.</div>
                ) : (
                  <div className="space-y-2">
                    {data.byCategory.map((c, i) => {
                      const max = data.byCategory[0]?.amount || 1;
                      const pct = (c.amount / max) * 100;
                      return (
                        <div key={c.category} data-testid={`exp-cat-${c.category}`}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="capitalize font-medium">{c.category}</span>
                            <span className="tabular-nums text-muted-foreground">₹{compactINR(c.amount)}</span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {expensesOpen && <ExpensesDialog onClose={() => setExpensesOpen(false)} />}
    </AppShell>
  );
}

function Kpi({ color, icon, label, value, testId }) {
  const tones = {
    emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-800",
    rose: "from-rose-50 to-white border-rose-200 text-rose-800",
    sky: "from-sky-50 to-white border-sky-200 text-sky-800",
  }[color];
  return (
    <div data-testid={testId} className={`bg-gradient-to-br ${tones} border rounded-2xl p-5 shadow-sm hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold mb-2">
        {icon}{label}
      </div>
      <div className="font-display font-extrabold text-3xl flex items-baseline gap-0.5">
        <IndianRupee className="h-5 w-5" />{(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
      </div>
    </div>
  );
}

function compactINR(n) {
  if (n >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return (n || 0).toFixed(0);
}

function arcPath(cx, cy, r, startAngle, endAngle) {
  const toRad = (a) => (a - 90) * Math.PI / 180;
  const x1 = cx + r * Math.cos(toRad(startAngle));
  const y1 = cy + r * Math.sin(toRad(startAngle));
  const x2 = cx + r * Math.cos(toRad(endAngle));
  const y2 = cy + r * Math.sin(toRad(endAngle));
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}
